import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Check, AlertTriangle, RefreshCw, 
  Scan, CheckCircle2, XCircle, Info, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const InhouseQtyConfirmation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferStatus, setTransferStatus] = useState(null);
  
  // Scanning state
  const [scanMode, setScanMode] = useState('normal'); // 'normal' or 'mismatch'
  const [usnInput, setUsnInput] = useState('');
  const [currentBundle, setCurrentBundle] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Mismatch scanning state
  const [mismatchScannedUsns, setMismatchScannedUsns] = useState([]);
  const [mismatchDialog, setMismatchDialog] = useState(false);
  
  const inputRef = useRef(null);

  useEffect(() => {
    fetchPendingTransfers();
  }, []);

  useEffect(() => {
    if (selectedTransfer) {
      fetchTransferStatus(selectedTransfer.transfer_id);
    }
  }, [selectedTransfer]);

  const fetchPendingTransfers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/inhouse/qty-confirmation/pending-transfers');
      setPendingTransfers(response.data.transfers || []);
    } catch (error) {
      toast.error('Failed to fetch pending transfers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferStatus = async (transferId) => {
    try {
      const response = await apiClient.get(`/inhouse/qty-confirmation/transfer/${transferId}/status`);
      setTransferStatus(response.data);
    } catch (error) {
      console.error('Error fetching transfer status:', error);
    }
  };

  const handleScanUSN = async () => {
    if (!usnInput.trim() || !selectedTransfer) return;
    
    const usn = usnInput.trim().toUpperCase();
    
    if (scanMode === 'mismatch') {
      // In mismatch mode, just add to scanned list
      if (mismatchScannedUsns.includes(usn)) {
        toast.warning('USN already scanned');
        return;
      }
      setMismatchScannedUsns([...mismatchScannedUsns, usn]);
      setUsnInput('');
      toast.success(`USN ${usn} scanned (${mismatchScannedUsns.length + 1} total)`);
      inputRef.current?.focus();
      return;
    }
    
    // Normal mode - lookup USN
    try {
      setProcessing(true);
      const response = await apiClient.post('/inhouse/qty-confirmation/lookup-usn', {
        usn_code: usn,
        transfer_id: selectedTransfer.transfer_id
      });
      
      setCurrentBundle({
        ...response.data,
        scanned_usn: usn
      });
      setUsnInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lookup USN');
      setUsnInput('');
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  };

  const handleAcceptBundle = async () => {
    if (!currentBundle || !selectedTransfer) return;
    
    try {
      setProcessing(true);
      const response = await apiClient.post('/inhouse/qty-confirmation/accept-bundle', {
        transfer_id: selectedTransfer.transfer_id,
        bundle_barcode: currentBundle.bundle_barcode,
        usn_code: currentBundle.scanned_usn
      });
      
      toast.success(response.data.message);
      setCurrentBundle(null);
      fetchTransferStatus(selectedTransfer.transfer_id);
      
      if (response.data.is_completed) {
        toast.success('All bundles confirmed! Qty confirmation completed.');
        fetchPendingTransfers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept bundle');
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  };

  const handleStartMismatch = () => {
    setScanMode('mismatch');
    setMismatchScannedUsns([]);
    toast.info('Mismatch mode: Scan ALL USNs in this bundle');
    inputRef.current?.focus();
  };

  const handleCompleteMismatch = async () => {
    if (!currentBundle || !selectedTransfer) return;
    
    try {
      setProcessing(true);
      const response = await apiClient.post('/inhouse/qty-confirmation/report-mismatch', {
        transfer_id: selectedTransfer.transfer_id,
        bundle_barcode: currentBundle.bundle_barcode,
        scanned_usns: mismatchScannedUsns
      });
      
      toast.success(`Mismatch reported: ${response.data.missing_count} USNs missing`);
      setMismatchDialog(false);
      setScanMode('normal');
      setMismatchScannedUsns([]);
      setCurrentBundle(null);
      fetchTransferStatus(selectedTransfer.transfer_id);
      
      if (response.data.is_completed) {
        fetchPendingTransfers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report mismatch');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelMismatch = () => {
    setScanMode('normal');
    setMismatchScannedUsns([]);
    setCurrentBundle(null);
    toast.info('Mismatch scanning cancelled');
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScanUSN();
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/inhouse')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Inward Qty Confirmation</h1>
              <p className="text-sm text-[#E8DCC8]">Inhouse Section - Mandatory Operation</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchPendingTransfers}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {!selectedTransfer ? (
          // Transfer selection view
          <div className="space-y-6">
            <Card className="border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#2D5F4F]" />
                  Pending Transfers for Qty Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-[#2D5F4F]" />
                  </div>
                ) : pendingTransfers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p>All transfers have been confirmed!</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {pendingTransfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id}
                        className="border border-[#E5DFD0] rounded-lg p-4 hover:border-[#2D5F4F] cursor-pointer transition-colors"
                        onClick={() => setSelectedTransfer(transfer)}
                        data-testid={`transfer-${transfer.transfer_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-[#1A1F2E]">{transfer.transfer_id}</h3>
                            <p className="text-sm text-gray-500">From: {transfer.from_section}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="mb-1">
                              {transfer.confirmation_progress?.confirmed || 0} / {transfer.confirmation_progress?.total || 0} Confirmed
                            </Badge>
                            <p className="text-xs text-gray-500">
                              {new Date(transfer.created_at).toLocaleDateString('en-IN')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          // Scanning view
          <div className="space-y-6">
            {/* Transfer Info */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Transfer: {selectedTransfer.transfer_id}
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setSelectedTransfer(null);
                      setCurrentBundle(null);
                      setScanMode('normal');
                    }}
                  >
                    Back to List
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">From Section</p>
                    <p className="font-medium">{selectedTransfer.from_section}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Bundles</p>
                    <p className="font-medium">{transferStatus?.total_bundles || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Confirmed</p>
                    <p className="font-medium text-green-600">{transferStatus?.confirmed_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="font-medium text-orange-600">{transferStatus?.pending_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scanning Section */}
            <Card className="border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scan className="w-5 h-5" />
                  {scanMode === 'mismatch' ? 'Mismatch Scan Mode' : 'Scan USN'}
                  {scanMode === 'mismatch' && (
                    <Badge className="bg-orange-500 ml-2">Scanning All USNs</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={usnInput}
                    onChange={(e) => setUsnInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={scanMode === 'mismatch' ? "Scan all USNs in the bundle..." : "Scan any USN from a bundle..."}
                    className="font-mono"
                    autoFocus
                    data-testid="usn-input"
                  />
                  <Button 
                    onClick={handleScanUSN}
                    disabled={processing || !usnInput.trim()}
                    className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
                    data-testid="scan-button"
                  >
                    {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                  </Button>
                </div>

                {scanMode === 'mismatch' && mismatchScannedUsns.length > 0 && (
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Scanned USNs: {mismatchScannedUsns.length}</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setMismatchDialog(true)}
                        className="bg-orange-500 text-white hover:bg-orange-600"
                      >
                        Complete Mismatch Scan
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                      {mismatchScannedUsns.map((usn, idx) => (
                        <Badge key={idx} variant="secondary" className="font-mono text-xs">
                          {usn}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleCancelMismatch}
                      className="mt-2 text-red-600"
                    >
                      Cancel Mismatch Scan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bundle Info Card */}
            {currentBundle && scanMode === 'normal' && (
              <Card className="border-[#2D5F4F] border-2">
                <CardHeader className="bg-[#2D5F4F]/10">
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-[#2D5F4F]" />
                    Bundle Found
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <p className="text-xs text-gray-500">Bundle Barcode</p>
                      <p className="font-mono font-bold text-lg">{currentBundle.bundle_barcode}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">USN Count</p>
                      <p className="font-bold text-2xl text-[#2D5F4F]">{currentBundle.usn_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Source Section</p>
                      <p className="font-medium">{currentBundle.source_section}</p>
                    </div>
                    {currentBundle.design_name && (
                      <div>
                        <p className="text-xs text-gray-500">Design</p>
                        <p className="font-medium">{currentBundle.design_name}</p>
                      </div>
                    )}
                    {currentBundle.sort_no && (
                      <div>
                        <p className="text-xs text-gray-500">Sort No</p>
                        <p className="font-medium">{currentBundle.sort_no}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={handleAcceptBundle}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="accept-button"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept Qty ({currentBundle.usn_count} USNs)
                    </Button>
                    <Button
                      onClick={handleStartMismatch}
                      disabled={processing}
                      variant="outline"
                      className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                      data-testid="mismatch-button"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Qty Mismatch
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bundle Status List */}
            {transferStatus?.bundles && (
              <Card className="border-[#E5DFD0]">
                <CardHeader>
                  <CardTitle>Bundle Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {transferStatus.bundles.map((bundle) => (
                      <div 
                        key={bundle.bundle_barcode}
                        className={`p-2 rounded border text-center text-sm font-mono ${
                          bundle.status === 'confirmed' 
                            ? 'bg-green-50 border-green-300 text-green-700'
                            : bundle.status === 'mismatch'
                            ? 'bg-orange-50 border-orange-300 text-orange-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                        }`}
                      >
                        {bundle.bundle_barcode.split('/')[0].replace('BUN', '')}
                        {bundle.status === 'confirmed' && <CheckCircle2 className="w-3 h-3 inline ml-1" />}
                        {bundle.status === 'mismatch' && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Mismatch Completion Dialog */}
      <Dialog open={mismatchDialog} onOpenChange={setMismatchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Confirm Quantity Mismatch
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">Expected</p>
                <p className="text-2xl font-bold">{currentBundle?.usn_count || 0}</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded">
                <p className="text-xs text-gray-500">Scanned</p>
                <p className="text-2xl font-bold text-orange-600">{mismatchScannedUsns.length}</p>
              </div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded">
              <p className="text-xs text-gray-500">Missing</p>
              <p className="text-2xl font-bold text-red-600">
                {(currentBundle?.usn_count || 0) - mismatchScannedUsns.length}
              </p>
            </div>
            <p className="text-sm text-gray-600">
              This will report the missing USNs to <strong>{selectedTransfer?.from_section}</strong> and notify the <strong>Accounts</strong> team.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMismatchDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteMismatch}
              disabled={processing}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Report Mismatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InhouseQtyConfirmation;
